import { ScrollToTop } from './ScrollToTop';
import { Navigate, Route, Routes } from 'react-router-dom';
import { APP_LAYOUT_STRUCTURE } from '../../App/WEB/_Layouts/AppLayoutStructure';
import { APP__PLUGINS } from "../_RuntimeConfig/_Config/Plugins.js";
import { LAYOUTS } from "../_RuntimeConfig/_Config/_Layouts.js";
import { AppLayoutPublic } from '../../App/WEB/_Layouts/AppLayoutPublic';
import { AppGuard, AppGuestGuard } from '../Contexts/_Auth/_App/_index';
import { AppLayoutPrivate } from '../../App/WEB/_Layouts/AppLayoutPrivate';
import { useUserAuthenticated } from '../Contexts/_Auth/userAuthenticatedContext';
import { ScreenForbidden, ScreenNotFound, ScreenMaintenance, PageLoader } from '../../App/WEB/_Templates/_UI/Screen/_index';
import { USER_HOME_HELPER } from '../../_Helpers/Helpers/userHome.helper';

const IS_AUTH_MODULE_ENABLED = APP__PLUGINS.HAS_AUTH_USER_SIMPLE || APP__PLUGINS.HAS_AUTH_USER_PORTAL_ROL || APP__PLUGINS.HAS_AUTH_USER_PORTAL_CLIENT;

const ViewPlaceholder = ({ label, path }) => (
    <div style={{ padding: '48px 0'}}>
        <h1 style={{ fontSize: 28, margin: 0 }}>{label}</h1>
        <p style={{ opacity: .7 }}>Ruta <code>{path}</code> — vista pendiente de conectar.</p>
    </div>
);

const onFlattenRoutes = (items) => items.flatMap((item) => [
    item, ...(item.children ?? []).map((child) => ({ ...item, ...child, children: [] })),
]);

const onRenderRoutes = (items) => onFlattenRoutes(items).map((item) => (
    <Route key={item.path} path={item.path} element={item.component ?? <ViewPlaceholder label={item.label} path={item.path} />} />
));

const onRenderGuardedRoutes = (items, userAuth = null) => onFlattenRoutes(items).map((item) => {

    const BASE_ELEMENT = item.component ?? <ViewPlaceholder label={item.label} path={item.path} />;
    const MUST_CHANGE_PASSWORD = !!userAuth?.isAuthenticated && !!userAuth?.data?.mustChangePassword;
    const ELEMENT = MUST_CHANGE_PASSWORD && item.path !== "/change-password"
        ? <Navigate to="/change-password" replace />
        : BASE_ELEMENT;

    if (!APP_LAYOUT_STRUCTURE.guards.isEnabled) {
        return <Route key={item.path} path={item.path} element={ELEMENT} />;
    }

    return (
        <Route key={item.path} path={item.path} element={
            <AppGuard roles={item.roles} permissions={item.permissions}
                redirectTo={APP_LAYOUT_STRUCTURE.guards.loginPath}
                forbiddenTo={APP_LAYOUT_STRUCTURE.guards.forbiddenPath} >
                {ELEMENT}
            </AppGuard>
        } />
    );
});

const onRenderPublicRoutes = (items) => onFlattenRoutes(items).map((item) => {

    const ELEMENT = item.component ?? <ViewPlaceholder label={item.label} path={item.path} />;
    const IS_GUEST_ONLY = item.isBlockedWhenLogged || APP_LAYOUT_STRUCTURE.guards.guestOnlyPaths.includes(item.path);

    if (!APP_LAYOUT_STRUCTURE.guards.isEnabled || !IS_GUEST_ONLY) {
        return <Route key={item.path} path={item.path} element={ELEMENT} />;
    }

    return (
        <Route key={item.path} path={item.path} element={
            <AppGuestGuard redirectTo={APP_LAYOUT_STRUCTURE.guards.afterLoginPath}>
                {ELEMENT}
            </AppGuestGuard>
        } />
    );
});

export const AppRouting = () => {

    const { useDataUserAuth: USER_AUTH, onCallbackCloseSessionUserAuth: ON_LOGOUT, } = useUserAuthenticated();

    if (LAYOUTS.ROUTING.HAS_MAINTENANCE_MODE === true) return <ScreenMaintenance />;

    return (

        <>
            <ScrollToTop />

            <Routes>
                <Route element={<AppLayoutPublic />}>
                    {onRenderPublicRoutes(APP_LAYOUT_STRUCTURE.publicRoutes)}
                </Route>

                <Route element={
                    <AppLayoutPrivate isAuth={USER_AUTH?.isAuthenticated} user={USER_AUTH} onClickLogout={ON_LOGOUT} />
                }>
                    {onRenderGuardedRoutes(APP_LAYOUT_STRUCTURE.privateRoutes, USER_AUTH)}
                </Route>

                {!APP_LAYOUT_STRUCTURE.publicRoutes.some((item) => item.path === "/") && (
                    <Route path="/" element={
                        IS_AUTH_MODULE_ENABLED && USER_AUTH?.isLoading ? <PageLoader isFullScreen />
                            : <Navigate to={IS_AUTH_MODULE_ENABLED ? (
                                USER_AUTH?.isAuthenticated ? USER_HOME_HELPER.HOME(USER_AUTH?.data, APP_LAYOUT_STRUCTURE.guards.afterLoginPath)
                                    : (APP_LAYOUT_STRUCTURE.guards.guestEntryPath ?? APP_LAYOUT_STRUCTURE.guards.afterLoginPath)
                            ) : APP_LAYOUT_STRUCTURE.guards.afterLoginPath} replace />
                    } />
                )}

                <Route element={<AppLayoutPublic />}>

                    <Route path={APP_LAYOUT_STRUCTURE.guards.forbiddenPath} element={<ScreenForbidden backTo="/" />} />

                    {
                        LAYOUTS.ROUTING.HAS_REDIRECT_WHEN_PAGE_404 ? <Route path="*" element={<Navigate to="/" replace />} /> : <Route path="*" element={<ScreenNotFound homePath="/" />} />
                    }

                </Route>

            </Routes>
        </>
    );
};
